import { useEffect, useMemo, useState } from "react";

import PageHeader from "../components/PageHeader.jsx";
import apiClient from "../services/api.js";

const today = () => new Date().toISOString().slice(0, 10);

const buildFormState = () => ({
  name: "",
  target_amount: "",
  frequency: "monthly",
  start_date: today(),
  duration_years: "0",
  duration_months: "6"
});

const buildDepositState = () => ({
  saving_id: "",
  amount: ""
});

const frequencyLabel = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly"
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 2
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "Not set";
  return new Intl.DateTimeFormat("en-NG", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(value));
};

const addMonths = (date, months) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months, 1);
  next.setDate(Math.min(date.getDate(), 28));
  return next;
};

const getInstallments = (startDate, maturityDate, frequency) => {
  if (!startDate || !maturityDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${maturityDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  const days = Math.floor((end - start) / 86400000) + 1;
  if (frequency === "daily") return Math.max(days, 1);
  if (frequency === "weekly") return Math.max(Math.ceil(days / 7), 1);
  let installments = 0;
  while (addMonths(start, installments) <= end) {
    installments += 1;
  }
  return Math.max(installments, 1);
};

const getMaturityDate = (form) => {
  const years = Number(form.duration_years || 0);
  const months = Number(form.duration_months || 0);
  const totalMonths = years * 12 + months;
  if (!form.start_date || totalMonths <= 0) return "";
  const start = new Date(`${form.start_date}T00:00:00`);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start);
  end.setMonth(end.getMonth() + totalMonths, 1);
  end.setDate(Math.min(start.getDate(), 28));
  end.setDate(end.getDate() - 1);
  return end.toISOString().slice(0, 10);
};

const getPlanMath = (form, maturityDate) => {
  const target = Number(form.target_amount || 0);
  const installments = getInstallments(form.start_date, maturityDate, form.frequency);
  const scheduledAmount = target > 0 && installments > 0 ? Math.ceil((target / installments) * 100) / 100 : 0;
  return { target, installments, scheduledAmount };
};

const buildScheduleRows = (goal) => {
  if (!goal) return [];
  const start = new Date(`${goal.start_date || goal.created_at?.slice(0, 10) || today()}T00:00:00`);
  const installments = Number(goal.total_installments || 0);
  const target = Number(goal.target_amount || 0);
  const current = Number(goal.current_amount || 0);
  const scheduled = Number(goal.scheduled_amount || 0);
  let cumulative = 0;

  return Array.from({ length: installments }, (_, index) => {
    let due = new Date(start);
    if (goal.frequency === "daily") due.setDate(start.getDate() + index);
    if (goal.frequency === "weekly") due.setDate(start.getDate() + index * 7);
    if (goal.frequency === "monthly") due = addMonths(start, index);

    const remainingTarget = Math.max(target - cumulative, 0);
    const amount = index === installments - 1 ? remainingTarget : Math.min(scheduled, remainingTarget);
    const before = cumulative;
    cumulative += amount;
    const paidAmount = Math.max(Math.min(current - before, amount), 0);
    const status = paidAmount >= amount ? "paid" : paidAmount > 0 ? "partial" : "upcoming";

    return {
      id: `${goal.id}-${index}`,
      number: index + 1,
      due_date: due.toISOString().slice(0, 10),
      amount,
      paidAmount,
      cumulative,
      status
    };
  });
};

export default function SavingsPage() {
  const [savings, setSavings] = useState([]);
  const [form, setForm] = useState(() => buildFormState());
  const [depositForm, setDepositForm] = useState(() => buildDepositState());
  const [selectedGoalId, setSelectedGoalId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const [scheduleGoal, setScheduleGoal] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadSavings = async () => {
    const { data } = await apiClient.get("/savings/");
    setSavings(data || []);
    setSelectedGoalId((current) => current || data?.[0]?.id?.toString() || "");
    setDepositForm((current) => ({
      ...current,
      saving_id: current.saving_id || data?.[0]?.id?.toString() || ""
    }));
  };

  useEffect(() => {
    loadSavings().catch(() => {});
  }, []);

  const maturityDate = useMemo(() => getMaturityDate(form), [form]);
  const planMath = useMemo(() => getPlanMath(form, maturityDate), [form, maturityDate]);
  const selectedGoal = savings.find((goal) => goal.id?.toString() === selectedGoalId) || savings[0];
  const depositGoal = savings.find((goal) => goal.id?.toString() === depositForm.saving_id);

  const totals = useMemo(() => {
    const target = savings.reduce((sum, goal) => sum + Number(goal.target_amount || 0), 0);
    const current = savings.reduce((sum, goal) => sum + Number(goal.current_amount || 0), 0);
    const active = savings.filter((goal) => goal.status === "active").length;
    return {
      target,
      current,
      active,
      progress: target > 0 ? Math.min((current / target) * 100, 100) : 0
    };
  }, [savings]);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");
    setError("");
    try {
      const { data } = await apiClient.post("/savings/", {
        name: form.name,
        frequency: form.frequency,
        start_date: form.start_date,
        maturity_date: maturityDate,
        target_amount: Number(form.target_amount),
        scheduled_amount: planMath.scheduledAmount
      });
      setSavings((prev) => [data, ...prev]);
      setSelectedGoalId(data.id.toString());
      setDepositForm({ saving_id: data.id.toString(), amount: data.scheduled_amount || "" });
      setForm(buildFormState());
      setStatus("Target savings plan created.");
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to create target savings plan.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeposit = async (event) => {
    event.preventDefault();
    if (!depositForm.saving_id) return;
    setDepositing(true);
    setStatus("");
    setError("");
    try {
      const { data } = await apiClient.post(`/savings/${depositForm.saving_id}/contribute`, {
        amount: Number(depositForm.amount)
      });
      setSavings((prev) => prev.map((goal) => (goal.id === data.id ? data : goal)));
      setSelectedGoalId(data.id.toString());
      setDepositForm((prev) => ({ ...prev, amount: data.scheduled_amount || "" }));
      setStatus("Deposit recorded in wallet transactions.");
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to record deposit.");
    } finally {
      setDepositing(false);
    }
  };

  return (
    <div className="space-y-5 fade-up md:space-y-8">
      <PageHeader
        title="Target savings"
        description="Plan a goal, choose a contribution rhythm, and track every deposit against the target."
      />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
        <MetricCard label="Total saved" value={formatMoney(totals.current)} helper={`${totals.progress.toFixed(1)}% of all goals`} />
        <MetricCard label="Target value" value={formatMoney(totals.target)} helper={`${savings.length} savings goals`} />
        <MetricCard label="Active plans" value={totals.active} helper="Daily, weekly, or monthly" />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <form onSubmit={handleSubmit} className="card-surface rounded-3xl p-5 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Savings calculator</p>
              <h2 className="mt-2 font-display text-3xl text-accent">Build a target plan</h2>
            </div>
            <div className="rounded-2xl bg-white/80 px-4 py-3 text-right">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">You save</p>
              <p className="text-2xl font-semibold text-ink">{formatMoney(planMath.scheduledAmount)}</p>
              <p className="text-xs text-slate-500">{frequencyLabel[form.frequency]}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Field label="Goal name">
              <input name="name" value={form.name} onChange={handleChange} placeholder="Rent, school fees, equipment" required className="input" />
            </Field>
            <Field label="Target amount">
              <input name="target_amount" type="number" min="1" step="0.01" value={form.target_amount} onChange={handleChange} placeholder="500000" required className="input" />
            </Field>
            <Field label="Start date">
              <input name="start_date" type="date" value={form.start_date} onChange={handleChange} required className="input" />
            </Field>
            <Field label="Years">
              <input name="duration_years" type="number" min="0" step="1" value={form.duration_years} onChange={handleChange} required className="input" />
            </Field>
            <Field label="Months">
              <input name="duration_months" type="number" min="0" step="1" value={form.duration_months} onChange={handleChange} required className="input" />
            </Field>
            <Field label="Savings rhythm">
              <select name="frequency" value={form.frequency} onChange={handleChange} className="input">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </Field>
            <div className="rounded-2xl border border-black/10 bg-white/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Plan estimate</p>
              <p className="mt-2 text-sm text-slate-600">
                {planMath.installments || 0} expected deposits of{" "}
                <span className="font-semibold text-ink">{formatMoney(planMath.scheduledAmount)}</span>
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Ends automatically on <span className="font-semibold text-ink">{formatDate(maturityDate)}</span>
              </p>
            </div>
          </div>

          {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
          {status && <p className="mt-4 text-sm text-emerald-600">{status}</p>}
          <button
            type="submit"
            disabled={submitting || !planMath.installments || !maturityDate}
            className="mt-6 w-full rounded-2xl bg-accent px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white disabled:opacity-50 md:w-auto"
          >
            {submitting ? "Creating..." : "Initiate plan"}
          </button>
        </form>

        <form onSubmit={handleDeposit} className="card-surface rounded-3xl p-5 md:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Wallet transaction</p>
          <h2 className="mt-2 font-display text-3xl text-accent">Deposit to target</h2>
          <div className="mt-6 space-y-4">
            <Field label="Target savings">
              <select
                value={depositForm.saving_id}
                onChange={(event) => {
                  const goal = savings.find((entry) => entry.id?.toString() === event.target.value);
                  setDepositForm({
                    saving_id: event.target.value,
                    amount: goal?.scheduled_amount || ""
                  });
                }}
                className="input"
                required
              >
                <option value="">Select target</option>
                {savings.map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name} - {frequencyLabel[goal.frequency] || goal.frequency}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Deposit amount">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={depositForm.amount}
                onChange={(event) => setDepositForm((prev) => ({ ...prev, amount: event.target.value }))}
                className="input"
                required
              />
            </Field>
            {depositGoal && (
              <div className="rounded-2xl bg-white/75 p-4 text-sm text-slate-600">
                <p className="font-semibold text-ink">{depositGoal.name}</p>
                <p className="mt-1">
                  Next expected deposit: {formatMoney(depositGoal.scheduled_amount)} on{" "}
                  {formatDate(depositGoal.next_due_date)}
                </p>
              </div>
            )}
            <button
              type="submit"
              disabled={depositing || !savings.length}
              className="w-full rounded-2xl bg-accent px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white disabled:opacity-50"
            >
              {depositing ? "Recording..." : "Record deposit"}
            </button>
          </div>
        </form>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <GoalFocus
          goal={selectedGoal}
          onSelect={setSelectedGoalId}
          onOpenSchedule={setScheduleGoal}
          savings={savings}
        />
        <GoalList goals={savings} selectedGoalId={selectedGoalId} onSelect={setSelectedGoalId} />
      </section>

      {scheduleGoal && (
        <ScheduleModal goal={scheduleGoal} onClose={() => setScheduleGoal(null)} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function MetricCard({ label, value, helper }) {
  return (
    <div className="card-surface rounded-3xl p-4 md:p-5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 md:text-xs">{label}</p>
      <p className="mt-2 text-xl font-semibold text-ink md:mt-3 md:text-3xl">{value}</p>
      <p className="mt-1 text-xs text-slate-500 md:text-sm">{helper}</p>
    </div>
  );
}

function GoalFocus({ goal, savings, onSelect, onOpenSchedule }) {
  if (!goal) {
    return (
      <section className="card-surface rounded-3xl p-6">
        <h2 className="font-display text-3xl text-accent">No active target yet</h2>
        <p className="mt-2 text-sm text-slate-600">Create a plan to see progress analytics here.</p>
      </section>
    );
  }
  const analytics = goal.analytics || {};
  const progress = Number(analytics.progress_percent || 0);
  return (
    <section className="card-surface rounded-3xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Goal analytics</p>
          <h2 className="mt-2 font-display text-3xl text-accent">{goal.name}</h2>
        </div>
        <select value={goal.id} onChange={(event) => onSelect(event.target.value)} className="input max-w-xs">
          {savings.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-ink">{formatMoney(goal.current_amount)}</span>
          <span className="text-slate-500">{formatMoney(goal.target_amount)}</span>
        </div>
        <div className="mt-3 h-4 overflow-hidden rounded-full bg-white">
          <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Insight label="Progress" value={`${progress.toFixed(1)}%`} />
        <Insight label="Remaining" value={formatMoney(analytics.remaining_amount)} />
        <Insight label="Paid deposits" value={`${analytics.paid_installments || 0} of ${goal.total_installments || 0}`} />
        <Insight label="Days left" value={analytics.days_remaining ?? 0} />
        <Insight label="Rhythm" value={frequencyLabel[goal.frequency] || goal.frequency} />
        <Insight label="Next due" value={formatDate(goal.next_due_date)} />
      </div>
      <button
        type="button"
        onClick={() => onOpenSchedule(goal)}
        className="mt-5 w-full rounded-2xl border border-accent/20 bg-white/80 px-5 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-accent transition hover:bg-white"
      >
        View savings schedule
      </button>
    </section>
  );
}

function Insight({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/75 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

function GoalList({ goals, selectedGoalId, onSelect }) {
  return (
    <section className="card-surface rounded-3xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Contribution progress</p>
          <h2 className="mt-2 font-display text-3xl text-accent">Savings goals</h2>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {goals.map((goal) => {
          const progress = Number(goal.analytics?.progress_percent || 0);
          const isSelected = goal.id?.toString() === selectedGoalId?.toString();
          return (
            <button
              type="button"
              key={goal.id}
              onClick={() => onSelect(goal.id.toString())}
              className={`w-full rounded-2xl border bg-white/75 p-4 text-left transition ${
                isSelected ? "border-accent shadow-sm" : "border-transparent hover:border-slate-200"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-ink">{goal.name}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {frequencyLabel[goal.frequency] || goal.frequency} - {formatMoney(goal.scheduled_amount)} per deposit
                  </p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  {goal.status}
                </span>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
              <div className="mt-3 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
                <span>{formatMoney(goal.current_amount)} saved</span>
                <span>{progress.toFixed(1)}%</span>
                <span>Ends {formatDate(goal.maturity_date)}</span>
              </div>
            </button>
          );
        })}
        {!goals.length && <p className="text-sm text-slate-500">No savings goals yet.</p>}
      </div>
    </section>
  );
}

function ScheduleModal({ goal, onClose }) {
  const rows = buildScheduleRows(goal);
  const paidRows = rows.filter((row) => row.status === "paid").length;
  const partialRows = rows.filter((row) => row.status === "partial").length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/45 px-3 py-6">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Savings schedule</p>
            <h2 className="mt-2 font-display text-2xl text-accent">{goal.name}</h2>
            <p className="mt-1 text-sm text-slate-600">
              {frequencyLabel[goal.frequency] || goal.frequency} deposits from {formatDate(goal.start_date)} to {formatDate(goal.maturity_date)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700"
          >
            Close
          </button>
        </div>

        <div className="grid gap-3 border-b border-slate-100 px-5 py-4 sm:grid-cols-4 sm:px-6">
          <Insight label="Target" value={formatMoney(goal.target_amount)} />
          <Insight label="Per deposit" value={formatMoney(goal.scheduled_amount)} />
          <Insight label="Paid rows" value={`${paidRows}${partialRows ? ` + ${partialRows} partial` : ""}`} />
          <Insight label="Installments" value={rows.length} />
        </div>

        <div className="max-h-[56vh] overflow-auto px-5 py-4 sm:px-6">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
                <th className="py-3 pr-4">No.</th>
                <th className="py-3 pr-4">Due date</th>
                <th className="py-3 pr-4">Payment</th>
                <th className="py-3 pr-4">Paid</th>
                <th className="py-3 pr-4">Cumulative</th>
                <th className="py-3 pr-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="py-3 pr-4 font-semibold text-ink">{row.number}</td>
                  <td className="py-3 pr-4 text-slate-600">{formatDate(row.due_date)}</td>
                  <td className="py-3 pr-4 text-slate-600">{formatMoney(row.amount)}</td>
                  <td className="py-3 pr-4 text-slate-600">{formatMoney(row.paidAmount)}</td>
                  <td className="py-3 pr-4 text-slate-600">{formatMoney(row.cumulative)}</td>
                  <td className="py-3 pr-4">
                    <ScheduleStatus value={row.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <p className="py-6 text-sm text-slate-500">No schedule available for this target.</p>}
        </div>
      </div>
    </div>
  );
}

function ScheduleStatus({ value }) {
  const styles = {
    paid: "bg-emerald-50 text-emerald-700",
    partial: "bg-amber-50 text-amber-700",
    upcoming: "bg-slate-100 text-slate-600"
  };
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${styles[value] || styles.upcoming}`}>
      {value}
    </span>
  );
}
